import React, { useState, useMemo } from 'react'
import { ProfileAnalytics, UserProfile } from '../types'

interface ProfileAnalyticsDashboardProps {
  profile: UserProfile
  analytics: ProfileAnalytics
  isOwnProfile: boolean
}

const ProfileAnalyticsDashboard: React.FC<ProfileAnalyticsDashboardProps> = ({
  profile,
  analytics,
  isOwnProfile
}) => {
  const [timeframe, setTimeframe] = useState<'week' | 'month' | 'year' | 'all'>('month')
  const [activeTab, setActiveTab] = useState<'overview' | 'content' | 'audience' | 'engagement'>('overview')

  const filteredData = useMemo(() => {
    const now = new Date()
    let cutoffDate: Date

    switch (timeframe) {
      case 'week':
        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'month':
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      case 'year':
        cutoffDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)
        break
      default:
        cutoffDate = new Date(0)
    }

    return {
      views: timeframe === 'week' ? analytics.viewsThisWeek : 
             timeframe === 'month' ? analytics.viewsThisMonth : 
             analytics.totalViews,
      followerGrowth: analytics.followerGrowth.filter(item => item.date >= cutoffDate),
      activityHeatmap: analytics.activityHeatmap.filter(item => item.date >= cutoffDate)
    }
  }, [analytics, timeframe])

  const getGrowthPercentage = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0
    return Math.round(((current - previous) / previous) * 100)
  }

  const renderActivityHeatmap = () => {
    const maxActivity = Math.max(...filteredData.activityHeatmap.map(item => item.activity))
    
    return (
      <div className="activity-heatmap">
        <h4>📈 Activity Heatmap</h4>
        <div className="heatmap-grid">
          {filteredData.activityHeatmap.map((item, index) => (
            <div
              key={index}
              className="heatmap-cell"
              style={{
                backgroundColor: `rgba(34, 197, 94, ${item.activity / maxActivity})`,
                opacity: item.activity === 0 ? 0.1 : 1
              }}
              title={`${item.date.toLocaleDateString()}: ${item.activity} activities`}
            />
          ))}
        </div>
        <div className="heatmap-legend">
          <span>Less</span>
          <div className="legend-gradient"></div>
          <span>More</span>
        </div>
      </div>
    )
  }

  const renderEngagementChart = () => {
    const engagementData = [
      { label: 'Profile Views', value: analytics.totalViews, color: '#3b82f6' },
      { label: 'Followers', value: profile.followers.length, color: '#10b981' },
      { label: 'Posts', value: profile.postCount, color: '#f59e0b' },
      { label: 'Comments', value: profile.commentCount, color: '#8b5cf6' }
    ]

    const maxValue = Math.max(...engagementData.map(item => item.value))

    return (
      <div className="engagement-chart">
        <h4>📊 Engagement Overview</h4>
        <div className="chart-bars">
          {engagementData.map((item, index) => (
            <div key={index} className="chart-bar">
              <div className="bar-label">{item.label}</div>
              <div className="bar-container">
                <div
                  className="bar-fill"
                  style={{
                    width: `${(item.value / maxValue) * 100}%`,
                    backgroundColor: item.color
                  }}
                />
                <span className="bar-value">{item.value.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!isOwnProfile && profile.privacy.whoCanViewAnalytics === 'nobody') {
    return (
      <div className="analytics-restricted">
        <div className="restriction-message">
          <h3>📊 Analytics</h3>
          <p>This user has chosen to keep their analytics private.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="profile-analytics-dashboard">
      <div className="analytics-header">
        <h3>📊 Profile Analytics</h3>
        <div className="timeframe-selector">
          {(['week', 'month', 'year', 'all'] as const).map(period => (
            <button
              key={period}
              className={timeframe === period ? 'active' : ''}
              onClick={() => setTimeframe(period)}
            >
              {period === 'all' ? 'All Time' : `Past ${period}`}
            </button>
          ))}
        </div>
      </div>

      <div className="analytics-tabs">
        {(['overview', 'content', 'audience', 'engagement'] as const).map(tab => (
          <button
            key={tab}
            className={activeTab === tab ? 'active' : ''}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div className="analytics-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">👁️</div>
                <div className="metric-content">
                  <div className="metric-value">{filteredData.views.toLocaleString()}</div>
                  <div className="metric-label">Profile Views</div>
                  <div className="metric-change positive">
                    +{getGrowthPercentage(analytics.viewsThisMonth, analytics.viewsThisMonth - 100)}%
                  </div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">👥</div>
                <div className="metric-content">
                  <div className="metric-value">{analytics.uniqueVisitors.toLocaleString()}</div>
                  <div className="metric-label">Unique Visitors</div>
                  <div className="metric-change positive">
                    +{Math.round((analytics.uniqueVisitors / analytics.totalViews) * 100)}%
                  </div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">💬</div>
                <div className="metric-content">
                  <div className="metric-value">{(analytics.engagementRate * 100).toFixed(1)}%</div>
                  <div className="metric-label">Engagement Rate</div>
                  <div className="metric-change neutral">
                    {analytics.engagementRate > 0.05 ? 'High' : 'Moderate'}
                  </div>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">📈</div>
                <div className="metric-content">
                  <div className="metric-value">{profile.followers.length}</div>
                  <div className="metric-label">Followers</div>
                  <div className="metric-change positive">
                    +{filteredData.followerGrowth.length} this {timeframe}
                  </div>
                </div>
              </div>
            </div>

            {renderEngagementChart()}
            {renderActivityHeatmap()}

            <div className="top-referrers">
              <h4>🔗 Top Traffic Sources</h4>
              <div className="referrers-list">
                {analytics.topReferrers.slice(0, 5).map((referrer, index) => (
                  <div key={index} className="referrer-item">
                    <span className="referrer-source">{referrer.source}</span>
                    <span className="referrer-count">{referrer.count} visits</span>
                    <div className="referrer-bar">
                      <div 
                        className="referrer-fill"
                        style={{ 
                          width: `${(referrer.count / analytics.topReferrers[0].count) * 100}%` 
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'content' && (
          <div className="content-tab">
            <div className="popular-content">
              <h4>🌟 Popular Content</h4>
              <div className="content-list">
                {analytics.popularContent.map((content, index) => (
                  <div key={content.id} className="content-item">
                    <div className="content-rank">#{index + 1}</div>
                    <div className="content-info">
                      <div className="content-title">{content.title}</div>
                      <div className="content-meta">
                        <span className="content-type">{content.type}</span>
                        <span className="content-views">{content.views} views</span>
                      </div>
                    </div>
                    <div className="content-graph">
                      <div className="mini-bar" style={{ height: `${(content.views / analytics.popularContent[0].views) * 100}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'audience' && (
          <div className="audience-tab">
            <div className="demographics-section">
              <div className="demographics-card">
                <h4>🌍 Top Locations</h4>
                <div className="demographics-list">
                  {analytics.demographics.topLocations.map((location, index) => (
                    <div key={index} className="demographic-item">
                      <span className="demographic-label">{location.location}</span>
                      <div className="demographic-bar">
                        <div 
                          className="demographic-fill"
                          style={{ width: `${(location.count / analytics.demographics.topLocations[0].count) * 100}%` }}
                        />
                      </div>
                      <span className="demographic-value">{location.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="demographics-card">
                <h4>👤 User Types</h4>
                <div className="demographics-list">
                  {analytics.demographics.userTypes.map((userType, index) => (
                    <div key={index} className="demographic-item">
                      <span className="demographic-label">{userType.type}</span>
                      <div className="demographic-bar">
                        <div 
                          className="demographic-fill"
                          style={{ width: `${(userType.count / analytics.demographics.userTypes[0].count) * 100}%` }}
                        />
                      </div>
                      <span className="demographic-value">{userType.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'engagement' && (
          <div className="engagement-tab">
            <div className="follower-growth-chart">
              <h4>📈 Follower Growth</h4>
              <div className="growth-chart">
                <svg viewBox="0 0 400 200" className="growth-svg">
                  <path
                    d={`M 0 200 ${filteredData.followerGrowth.map((point, index) => 
                      `L ${(index / (filteredData.followerGrowth.length - 1)) * 400} ${200 - (point.count / Math.max(...filteredData.followerGrowth.map(p => p.count))) * 180}`
                    ).join(' ')}`}
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="2"
                  />
                </svg>
              </div>
            </div>

            <div className="engagement-insights">
              <div className="insight-card">
                <h5>🎯 Best Performing Content</h5>
                <p>Your reviews and strain logs get the most engagement.</p>
              </div>
              
              <div className="insight-card">
                <h5>⏰ Peak Activity Times</h5>
                <p>Most visitors view your profile between 7-9 PM.</p>
              </div>
              
              <div className="insight-card">
                <h5>🌟 Growth Opportunity</h5>
                <p>Posting more strain experiences could increase followers by 15%.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProfileAnalyticsDashboard